import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTablePreferences } from "../hooks/useTablePreferences";
import { useListViews } from "../hooks/useListViews";
import { CustomButton } from "../components/ui/CustomButton";
import { User, ListView, ListViewData, MeetingType, ClinicUserApiResponse } from "../types/api";
import { Layout } from "../components/layout/Layout";
import { ManageColumnsModal, ColumnConfig } from "../components/modals/ManageColumnsModal";
import { SaveViewModal } from "../components/modals/SaveViewModal";
import { MeetingCountModal } from "../components/modals/MeetingCountModal";
import { IssuesModal } from "../components/modals/IssuesModal";
import { EditCommentsModal } from "../components/modals/EditCommentsModal";
import { EditPatientModal } from "../components/modals/EditPatientModal";
import { NewMeetingModal } from "../components/modals/NewMeetingModal";
import { ViewMeetingsModal } from "../components/modals/ViewMeetingsModal";
import { FinishTreatmentModal } from "../components/modals/FinishTreatmentModal";
import { CallPatientConfModal } from "../components/modals/CallPatientConfModal";
import { AdHocMeetingModal } from "../components/modals/AdHocMeetingModal";
import { ViewSelector } from "../components/ui/ViewSelector";
import { ContextMenu } from "../components/ui/ContextMenu";
import { WhatsAppConversationPopup } from "../components/whatsapp/WhatsAppConversationPopup";
import { normalizePhone } from "../utils/phoneUtils";
import apiService from "../services/api";
import { saveLastViewId, getLastViewId, clearLastViewId } from "../utils/viewPersistence";
import { canScheduleMeeting } from '../utils/permissions';
//tr
const DEFAULT_DAYS = 14;

interface AdHocMeetingsScreenProps {
    user: User;
    onLogout: () => void;
    onNavigate?: (screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections" | "billing" | "whatsapp" | "adhoc-meetings" | "ai-support", patientId?: string | number, meetingId?: string | number) => void;
    users: ClinicUserApiResponse[];
    meetingTypes: MeetingType[];
    clinics?: any;
    taskCount?: number;
    whatsappUnreadCount?: number;
}

export const AdHocMeetingsScreen: React.FC<AdHocMeetingsScreenProps> = ({
    user,
    onLogout,
    onNavigate,
    users: propsUsers,
    meetingTypes: propsMeetingTypes,
    clinics: propsClinics,
    taskCount,
    whatsappUnreadCount,
}) => {
    // Check if user is admin first (needed for state initialization)
    const isAdmin = useMemo(() => {
        return user?.role === 'admin' || user?.role === 'Admin';
    }, [user?.role]);

    // Calculate default "only mine" value based on user role and type
    const defaultOnlyMy = useMemo(() => {
        const isAdminRole = user?.role === 'admin' || user?.role === 'Admin';
        const isCaseManagerOrDoctor = user.type === 'doctor' || user.type === 'case_manager';
        return isAdminRole && isCaseManagerOrDoctor;
    }, [user?.role, user.type]);

    // The screen unmounts on navigation, so keep the last-used filters in
    // localStorage - otherwise coming back resets them (e.g. back to "Only mine",
    // which may show an empty list).
    const storedFilters = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('adhoc-patients-filters') || '{}');
        } catch {
            return {};
        }
    }, []);

    const [userId, setUserId] = useState<number>(storedFilters.userId ?? 0);
    const [onlyMy, setOnlyMy] = useState<boolean>(storedFilters.onlyMy ?? defaultOnlyMy);
    const [daysFromLastCmFollowup, setDaysFromLastCmFollowup] = useState<number>(storedFilters.daysFromLastCmFollowup ?? DEFAULT_DAYS);
    const [daysToNextCmFollowup, setDaysToNextCmFollowup] = useState<number>(storedFilters.daysToNextCmFollowup ?? DEFAULT_DAYS);

    React.useEffect(() => {
        localStorage.setItem('adhoc-patients-filters', JSON.stringify({
            userId,
            onlyMy,
            daysFromLastCmFollowup,
            daysToNextCmFollowup,
        }));
    }, [userId, onlyMy, daysFromLastCmFollowup, daysToNextCmFollowup]);

    // Define all available columns with their configurations
    // Same as the patients table, minus sub clinic / payment link / move status /
    // move clinic / funding entity / cancel reason / finish reason / cancel notes,
    // plus the last/next CM & PSY meeting/follow-up fields.
    const ALL_COLUMNS: ColumnConfig[] = [
        { id: "HM_ID", label: "HM ID", visible: true },
        { id: "first_name", label: "First name", visible: true },
        { id: "last_name", label: "Last name", visible: true },
        { id: "last_psy_meeting", label: "Last PSY Meeting", visible: true },
        { id: "next_psy_followup", label: "Next PSY Followup", visible: true },
        { id: "last_cm_followup", label: "Last CM Followup", visible: true },
        { id: "next_cm_followup", label: "Next CM Followup", visible: true },
        { id: "number_of_calls_tries", label: "Call Tries", visible: true },
        { id: "number_of_cm_followup", label: "CM Followups", visible: true },
        { id: "notes", label: "Notes", visible: true },
        { id: "invitation_status", label: "Invitation status", visible: true },
        { id: "issues", label: "Issues", visible: true },
        { id: "doctor_id", label: "Psychiatrist", visible: true },
        { id: "clinic_id", label: "Clinic", visible: true },
        { id: "patient_type", label: "Age group", visible: true },
        { id: "meeting_count", label: "Meeting number", visible: true },
        { id: "last_interaction", label: "Last meeting", visible: true },
        { id: "next_meeting_start", label: "Next meeting", visible: true },
        { id: "created_at", label: "Created date", visible: true },
        { id: "area", label: "Area", visible: false },
        { id: "contact_name", label: "Contact Name", visible: false },
        { id: "contact_phone", label: "Contact Phone", visible: false },
        { id: "contact_role", label: "Contact Role", visible: false },
        { id: "status", label: "Status", visible: true },
        { id: "case_manager_id", label: "Case Manager", visible: true },
    ];

    // Default preferences
    const defaultPreferences = {
        columnOrder: ALL_COLUMNS.map(col => col.id),
        columnWidths: {},
        columnVisibility: ALL_COLUMNS.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>),
        sortColumn: "last_cm_followup",
        sortOrder: "desc" as const,
    };

    // Use the preferences hook
    const { preferences, updatePreferences, resetToDefaults } = useTablePreferences({
        storageKey: 'adhoc-patients-table-preferences',
        defaultPreferences,
    });

    // Extract values from preferences
    const [sortColumn, setSortColumn] = useState(preferences.sortColumn);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">(preferences.sortOrder);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(preferences.columnWidths);
    const [columnOrder, setColumnOrder] = useState<string[]>(preferences.columnOrder);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(preferences.columnVisibility);

    const token = localStorage.getItem("token") || "";

    // Modal state
    const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);
    const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
    const [currentViewId, setCurrentViewId] = useState<number | null>(null);
    const [baseViewId, setBaseViewId] = useState<number | null>(null);
    const [isMeetingCountModalOpen, setIsMeetingCountModalOpen] = useState(false);
    const [selectedPatientForMeetings, setSelectedPatientForMeetings] = useState<any>(null);
    const [isIssuesModalOpen, setIsIssuesModalOpen] = useState(false);
    const [selectedPatientForIssues, setSelectedPatientForIssues] = useState<any>(null);
    const [isEditCommentsModalOpen, setIsEditCommentsModalOpen] = useState(false);
    const [selectedPatientForEdit, setSelectedPatientForEdit] = useState<any>(null);
    const [isSavingComments, setIsSavingComments] = useState(false);
    const [isNewMeetingModalOpen, setIsNewMeetingModalOpen] = useState(false);
    const [selectedPatientForNewMeeting, setSelectedPatientForNewMeeting] = useState<any>(null);
    const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
    const [selectedPatientForEditInfo, setSelectedPatientForEditInfo] = useState<any>(null);
    const [isSavingPatientInfo, setIsSavingPatientInfo] = useState(false);
    const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
    const [isViewMeetingsModalOpen, setIsViewMeetingsModalOpen] = useState(false);
    const [selectedPatientForViewMeetings, setSelectedPatientForViewMeetings] = useState<any>(null);
    const [isFinishTreatmentModalOpen, setIsFinishTreatmentModalOpen] = useState(false);
    const [selectedPatientForFinishTreatment, setSelectedPatientForFinishTreatment] = useState<any>(null);
    const [selectedPatientForConfCall, setSelectedPatientForConfCall] = useState<any>(null);
    const [selectedPatientForAdHoc, setSelectedPatientForAdHoc] = useState<any>(null);

    // Context menu state
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; patient: any } | null>(null);
    const [waPopupPatient, setWaPopupPatient] = useState<{ phone: string; name?: string; hmId?: string; id?: number } | null>(null);

    // Status message state
    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'loading'; text: string } | null>(null);

    // List Views integration
    const {
        privateViews,
        publicViews,
        defaultView,
        createListView,
        updateListView,
        deleteListView,
        isLoading: isLoadingViews,
        isCreating,
    } = useListViews({
        userId: user?.id as number,
        listName: 'adhoc-patients',
        token,
        enabled: !!user?.id && !!token,
    });

    // Track if we've already loaded the initial view to prevent re-loading when view is cleared
    const hasLoadedInitialView = React.useRef(false);

    // Load saved view on mount
    React.useEffect(() => {
        if (isLoadingViews) {
            return;
        }
        if (hasLoadedInitialView.current) {
            return;
        }

        const savedViewId = getLastViewId('adhoc-patients');
        if (savedViewId !== null) {
            const allViews = [...privateViews, ...publicViews];
            const savedView = allViews.find(v => v.id === savedViewId);
            if (savedView) {
                handleLoadView(savedView);
                hasLoadedInitialView.current = true;
            } else {
                clearLastViewId('adhoc-patients');
            }
        } else if (defaultView) {
            handleLoadView(defaultView);
            hasLoadedInitialView.current = true;
        } else {
            hasLoadedInitialView.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingViews, privateViews, publicViews, defaultView]);

    // Column resizing state
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);
    const [startX, setStartX] = useState<number>(0);
    const [startWidth, setStartWidth] = useState<number>(0);

    // Sync preferences when state changes
    React.useEffect(() => {
        updatePreferences({
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
        });
    }, [columnOrder, columnWidths, columnVisibility, sortColumn, sortOrder, updatePreferences]);

    // Use users from props instead of fetching separately
    const userNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        propsUsers.forEach((u: any) => {
            map[u.id] = u.name || u.email;
        });
        return map;
    }, [propsUsers]);

    const getUserName = (userId: number): string => {
        return userNameMap[userId] || "";
    };

    // Use clinics from props to create a clinic name map
    const clinicsMap = useMemo(() => {
        const map: Record<number, string> = {};
        (propsClinics?.items || []).forEach((c: any) => {
            map[c.id] = c.name;
        });
        return map;
    }, [propsClinics?.items]);

    const getClinicName = (clinicId: number): string => {
        return clinicsMap[clinicId] || "";
    };

    // Determine the user_id to send in the API request:
    // non-admins always see only their own patients.
    const ownUserId = typeof user.id === 'number' ? user.id : parseInt(user.id as string, 10);
    const requestUserId = !isAdmin ? ownUserId : (onlyMy ? ownUserId : userId);

    // Fetch the ad-hoc candidate patients (no paging)
    const patientsQuery = useQuery({
        queryKey: ['adhoc_patients', requestUserId, daysFromLastCmFollowup, daysToNextCmFollowup],
        queryFn: () =>
            apiService.getAdhocPatients({
                token,
                user_id: requestUserId,
                days_from_last_cm_followup: daysFromLastCmFollowup,
                days_to_next_cm_followup: daysToNextCmFollowup,
            }),
        enabled: !!token,
    });

    const handleRefresh = () => {
        patientsQuery.refetch();
    };

    // Transformation functions (same as patients table)
    const formatDate = (timestamp: number | undefined | null) => {
        if (!timestamp || timestamp === 0) return "";
        const date = new Date(timestamp);
        return date.toLocaleDateString("en-GB");
    };

    const formatInvitationStatus = (patient: any): string => {
        if (patient.next_meeting > 0) {
            return "next meeting scheduled";
        }
        return patient.invitation_status || "";
    };

    const formatTasksNumber = (patient: any): string | number => {
        try {
            const issues = patient.issues ? JSON.parse(patient.issues) : [];
            if (issues.length === 0) return "";
            if (issues.length === 1) return issues[0].issue || "";
            return issues.length;
        } catch {
            return patient.tasks?.length || "";
        }
    };

    const formatNextMeetingDateTime = (patient: any): string => {
        const nextMeetingStart = patient.next_meeting_start;

        if (!nextMeetingStart || nextMeetingStart === 0) {
            return "";
        }

        const meetingDate = new Date(nextMeetingStart);
        const now = new Date();

        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
        const tomorrowStart = new Date(todayEnd);
        const tomorrowEnd = new Date(tomorrowStart.getTime() + 24 * 60 * 60 * 1000);

        const formattedTime = meetingDate.toLocaleTimeString("en-GB", {
            hour: '2-digit',
            minute: '2-digit'
        });

        if (meetingDate >= todayStart && meetingDate < todayEnd) {
            return `Today at ${formattedTime}`;
        } else if (meetingDate >= tomorrowStart && meetingDate < tomorrowEnd) {
            return `Tomorrow at ${formattedTime}`;
        } else {
            const day = String(meetingDate.getDate()).padStart(2, '0');
            const month = String(meetingDate.getMonth() + 1).padStart(2, '0');
            const year = meetingDate.getFullYear();
            return `${day}/${month}/${year} ${formattedTime}`;
        }
    };

    const formatMeetingCount = (patient: any): string => {
        return patient.meeting_count_total_text || "";
    };

    const getAgeGroupColor = (patientType: string): string => {
        switch (patientType) {
            case 'Soldier': return '#442586';
            case 'Adult': return '#664ED1';
            case 'Kids': return '#CFC6F9';
            default: return '#D8A5E5';
        }
    };

    const formatNotes = (notes: string): string => {
        return notes && notes.trim() !== '' ? notes : 'Empty';
    };

    // Helper function to check if user can save to the current view
    const clearViewOnChange = () => {
        if (currentViewId === null) return;

        const allViews = [...privateViews, ...publicViews];
        const currentView = allViews.find(v => v.id === currentViewId);

        if (!currentView) return;

        const isPrivateView = currentView.type === 'private';
        const isPublicView = currentView.type === 'public';
        const canSaveToPublicView = isAdmin;

        const canSaveToView = isPrivateView || (isPublicView && canSaveToPublicView);

        if (!canSaveToView) {
            setCurrentViewId(null);
            clearLastViewId('adhoc-patients');
        }
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("desc");
        }
        clearViewOnChange();
    };

    // Client-side sorting (the endpoint returns the full list, no paging)
    const sortedPatients = useMemo(() => {
        const items = [...(patientsQuery.data || [])];
        items.sort((a: any, b: any) => {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];

            if (aValue === bValue) return 0;
            if (aValue === null || aValue === undefined || aValue === "") return 1;
            if (bValue === null || bValue === undefined || bValue === "") return -1;

            const comparison = aValue < bValue ? -1 : 1;
            return sortOrder === "asc" ? comparison : -comparison;
        });
        return items;
    }, [patientsQuery.data, sortColumn, sortOrder]);

    // Column resize handlers
    const handleResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(column);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    };

    React.useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingColumn) return;

            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff);

            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            setResizingColumn(null);
        };

        if (resizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [resizingColumn, startX, startWidth]);

    const ResizableHeader = ({ column, label, nonSortable = false }: { column: string; label: string; nonSortable?: boolean }) => {
        const isSorted = sortColumn === column;
        const columnRef = React.useRef<HTMLTableCellElement>(null);
        const width = columnWidths[column];

        const handleHeaderClick = (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('cursor-col-resize') || target.closest('.cursor-col-resize')) {
                return;
            }
            if (!nonSortable) {
                handleSort(column);
            }
        };

        return (
            <th
                ref={columnRef}
                onClick={handleHeaderClick}
                className={`relative px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap ${!nonSortable ? 'hover:bg-gray-100 cursor-pointer' : ''} select-none`}
                style={width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : {}}
            >
                <div className="flex items-center gap-1">
                    {label}
                    {!nonSortable && isSorted && (
                        <span className="text-blue-600">
                            {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                    )}
                </div>
                <div
                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                    onMouseDown={(e) => handleResizeStart(e, column, columnRef.current?.offsetWidth || 150)}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        );
    };

    // Modal handlers
    const handleManageColumns = () => {
        setIsManageColumnsOpen(true);
    };

    const handleSaveColumns = (columns: ColumnConfig[]) => {
        const newOrder = columns.map(col => col.id);
        const newVisibility = columns.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>);

        setColumnOrder(newOrder);
        setColumnVisibility(newVisibility);
    };

    const handleResetColumns = () => {
        setColumnOrder(defaultPreferences.columnOrder);
        setColumnWidths(defaultPreferences.columnWidths);
        setColumnVisibility(defaultPreferences.columnVisibility);
        setSortColumn(defaultPreferences.sortColumn);
        setSortOrder(defaultPreferences.sortOrder);

        // Also reset filters
        setDaysFromLastCmFollowup(DEFAULT_DAYS);
        setDaysToNextCmFollowup(DEFAULT_DAYS);
        setUserId(0);
        setOnlyMy(defaultOnlyMy);

        // Clear saved view
        setCurrentViewId(null);
        clearLastViewId('adhoc-patients');

        resetToDefaults();
    };

    // View management handlers
    const handleUpdateView = async () => {
        if (!baseViewId) return;

        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            filters: {
                daysFromLastCmFollowup,
                daysToNextCmFollowup,
            } as any,
        };

        try {
            await updateListView({
                id: baseViewId,
                data: viewData,
            });

            setCurrentViewId(baseViewId);
            saveLastViewId('adhoc-patients', baseViewId);
            hasLoadedInitialView.current = true;
        } catch (error) {
            console.error('Failed to update view:', error);
        }
    };

    const handleSaveAsView = async (name: string, type: 'private' | 'public', description?: string, isDefault?: boolean) => {
        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            description,
            filters: {
                daysFromLastCmFollowup,
                daysToNextCmFollowup,
            } as any,
            ...(isDefault && { is_default: true }),
        };

        try {
            const newView = await createListView({
                name,
                owner_id: user?.id as number,
                type,
                list_name: 'adhoc-patients',
                data: viewData,
            });

            setCurrentViewId(newView.id);
            setBaseViewId(newView.id);
            saveLastViewId('adhoc-patients', newView.id);
            hasLoadedInitialView.current = true;

            setIsSaveViewModalOpen(false);
        } catch (error) {
            console.error('Failed to save view:', error);
        }
    };

    const handleLoadView = (view: ListView) => {
        // Merge in any columns added since this view was saved so they still appear in Manage Columns.
        const savedOrder = view.data.columnOrder || [];
        const missingColumns = defaultPreferences.columnOrder.filter(id => !savedOrder.includes(id));
        setColumnOrder([...savedOrder, ...missingColumns]);
        setColumnWidths(view.data.columnWidths);
        setColumnVisibility({ ...defaultPreferences.columnVisibility, ...view.data.columnVisibility });
        setSortColumn(view.data.sortColumn);
        setSortOrder(view.data.sortOrder);

        const filters = view.data.filters as any;
        if (filters) {
            setDaysFromLastCmFollowup(filters.daysFromLastCmFollowup ?? DEFAULT_DAYS);
            setDaysToNextCmFollowup(filters.daysToNextCmFollowup ?? DEFAULT_DAYS);
        } else {
            setDaysFromLastCmFollowup(DEFAULT_DAYS);
            setDaysToNextCmFollowup(DEFAULT_DAYS);
        }

        setCurrentViewId(view.id);
        setBaseViewId(view.id);
        saveLastViewId('adhoc-patients', view.id);
    };

    const handleDeleteView = async (viewId: number) => {
        try {
            await deleteListView(viewId);
            if (currentViewId === viewId) {
                handleResetColumns();
                setCurrentViewId(null);
            }
        } catch (error) {
            console.error('Failed to delete view:', error);
        }
    };

    const renderPatientsTab = () => {
        if (patientsQuery.isLoading) {
            return (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading patients...</p>
                </div>
            );
        }

        if (patientsQuery.isError) {
            return (
                <div className="text-center py-12 text-red-600">
                    <p>Error loading patients. Please try again.</p>
                    <CustomButton
                        variant="secondary"
                        size="sm"
                        onClick={handleRefresh}
                        className="mt-4"
                    >
                        Retry
                    </CustomButton>
                </div>
            );
        }

        const patients = sortedPatients;

        return (
            <div className="space-y-4">
                {patients.length === 0 ? (
                    <div className="text-center py-12 text-gray-600">
                        <p>No patients found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => {
                                        const config = ALL_COLUMNS.find(col => col.id === columnId);
                                        if (!config) return null;
                                        const nonSortable = columnId === "issues" || columnId === "meeting_count" || columnId === "notes";

                                        return (
                                            <ResizableHeader
                                                key={columnId}
                                                column={columnId}
                                                label={config.label}
                                                nonSortable={nonSortable}
                                            />
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {patients.map((patient: any) => {
                                    const invitationStatus = formatInvitationStatus(patient);
                                    const tasksNumber = formatTasksNumber(patient);
                                    const caseManagerName = getUserName(patient.case_manager_id);
                                    const doctorName = getUserName(patient.doctor_id);
                                    const ageGroupColor = getAgeGroupColor(patient.patient_type);
                                    const meetingCount = formatMeetingCount(patient);
                                    const notes = formatNotes(patient.task);
                                    const lastMeeting = formatDate(patient.last_interaction);
                                    const nextMeeting = formatNextMeetingDateTime(patient);
                                    const createdDate = formatDate(patient.created_at);

                                    const getCellContent = (columnId: string) => {
                                        switch (columnId) {
                                            case "HM_ID":
                                                return patient.HM_ID ? (
                                                    <button
                                                        data-ai-id="adhoc-patient-id-link"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onNavigate?.("patient", patient.id);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                    >
                                                        {patient.HM_ID}
                                                    </button>
                                                ) : "-";
                                            case "first_name":
                                                return patient.first_name ? (
                                                    <button
                                                        data-ai-id="adhoc-patient-name-link"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            setIsLoadingPatientData(true);

                                                            await new Promise(resolve => setTimeout(resolve, 100));

                                                            try {
                                                                const patientData = await apiService.getPatientPage({
                                                                    token,
                                                                    patient_id: patient.id
                                                                });
                                                                setSelectedPatientForEditInfo(patientData);
                                                                setIsEditPatientModalOpen(true);
                                                            } catch (error) {
                                                                console.error('Failed to fetch patient data:', error);
                                                            } finally {
                                                                setIsLoadingPatientData(false);
                                                            }
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                        disabled={isLoadingPatientData}
                                                    >
                                                        {patient.first_name}
                                                    </button>
                                                ) : "-";
                                            case "last_name":
                                                return patient.last_name || "-";
                                            case "status":
                                                return patient.status ? (
                                                    <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">
                                                        {patient.status}
                                                    </span>
                                                ) : "-";
                                            case "invitation_status":
                                                return invitationStatus ? (
                                                    <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                                                        {invitationStatus}
                                                    </span>
                                                ) : "-";
                                            case "issues":
                                                if (!tasksNumber || tasksNumber === "-") return "-";

                                                return (
                                                    <button
                                                        data-ai-id="adhoc-task-count-link"
                                                        onClick={() => {
                                                            setSelectedPatientForIssues(patient);
                                                            setIsIssuesModalOpen(true);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                    >
                                                        {tasksNumber}
                                                    </button>
                                                );
                                            case "case_manager_id":
                                                return caseManagerName || "-";
                                            case "doctor_id":
                                                return doctorName || "-";
                                            case "clinic_id":
                                                return getClinicName(patient.clinic_id) || "-";
                                            case "patient_type":
                                                return patient.patient_type ? (
                                                    <span
                                                        className="px-2 py-1 text-xs rounded text-white"
                                                        style={{ backgroundColor: ageGroupColor }}
                                                    >
                                                        {patient.patient_type}
                                                    </span>
                                                ) : "-";
                                            case "meeting_count":
                                                return meetingCount ? (
                                                    <button
                                                        data-ai-id="adhoc-meeting-count-link"
                                                        onClick={() => {
                                                            setSelectedPatientForMeetings(patient);
                                                            setIsMeetingCountModalOpen(true);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                    >
                                                        {meetingCount}
                                                    </button>
                                                ) : "-";
                                            case "notes":
                                                return (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedPatientForEdit(patient);
                                                                setIsEditCommentsModalOpen(true);
                                                            }}
                                                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                                            title="Edit notes"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <span className="truncate max-w-xs" title={notes}>{notes}</span>
                                                    </div>
                                                );
                                            case "last_interaction":
                                                return lastMeeting || "-";
                                            case "next_meeting_start":
                                                return nextMeeting || "-";
                                            case "last_cm_followup":
                                                return formatDate(patient.last_cm_followup) || "-";
                                            case "next_cm_followup":
                                                return formatDate(patient.next_cm_followup) || "-";
                                            case "last_psy_meeting":
                                                return formatDate(patient.last_psy_meeting) || "-";
                                            case "next_psy_followup":
                                                return formatDate(patient.next_psy_followup) || "-";
                                            case "number_of_calls_tries":
                                                return patient.number_of_calls_tries ?? 0;
                                            case "number_of_cm_followup":
                                                return patient.number_of_cm_followup ?? "-";
                                            case "created_at":
                                                return createdDate || "-";
                                            case "area":
                                                return patient.area || "-";
                                            case "contact_name":
                                                return patient.contact_name || "-";
                                            case "contact_phone":
                                                return patient.contact_phone || "-";
                                            case "contact_role":
                                                return patient.contact_role || "-";
                                            default:
                                                return "-";
                                        }
                                    };

                                    return (
                                        <tr
                                            key={patient.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={(e) => {
                                                const target = e.target as HTMLElement;
                                                if (
                                                    target.tagName === 'BUTTON' ||
                                                    target.tagName === 'A' ||
                                                    target.closest('button') ||
                                                    target.closest('a')
                                                ) {
                                                    return;
                                                }

                                                setContextMenu({
                                                    x: e.clientX,
                                                    y: e.clientY,
                                                    patient: patient
                                                });
                                            }}
                                        >
                                            {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => (
                                                <td
                                                    key={columnId}
                                                    className="px-4 py-3 whitespace-nowrap text-sm text-gray-900"
                                                >
                                                    {getCellContent(columnId)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Layout
            userName={user?.name || user?.email || "User"}
            onLogout={onLogout}
            activeMenuItem="adhoc-meetings"
            onNavigate={onNavigate}
            institutionId={user.institution_id || 0}
            user={user}
            taskCount={taskCount}
            whatsappUnreadCount={whatsappUnreadCount}
        >
            {/* Ad Hoc Meetings Section */}
            <div className="bg-white rounded-lg shadow mb-8">
                <div className="border-b border-gray-200 px-6 py-4">
                    {/* Header row with title and view selector */}
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold text-gray-900">Ad Hoc Meetings</h2>
                            <ViewSelector
                                privateViews={privateViews}
                                publicViews={publicViews}
                                currentViewId={currentViewId}
                                baseViewId={baseViewId}
                                onSelectView={handleLoadView}
                                onSaveView={handleUpdateView}
                                onSaveAsView={() => setIsSaveViewModalOpen(true)}
                                onDeleteView={handleDeleteView}
                                isLoading={isLoadingViews}
                                isAdmin={isAdmin}
                            />
                        </div>
                        <CustomButton
                            variant="secondary"
                            size="sm"
                            onClick={handleManageColumns}
                        >
                            ⚙️ Manage Columns
                        </CustomButton>
                    </div>

                    {/* Controls row with filters */}
                    <div className="flex gap-4 items-center flex-wrap">
                        <label className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 whitespace-nowrap">Days from last CM followup</span>
                            <input
                                type="number"
                                min={0}
                                value={daysFromLastCmFollowup}
                                onChange={(e) => setDaysFromLastCmFollowup(Math.max(0, Number(e.target.value) || 0))}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </label>
                        <label className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 whitespace-nowrap">Days to next CM followup</span>
                            <input
                                type="number"
                                min={0}
                                value={daysToNextCmFollowup}
                                onChange={(e) => setDaysToNextCmFollowup(Math.max(0, Number(e.target.value) || 0))}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </label>

                        {/* User filter - only show for admins */}
                        {isAdmin && (
                            <div className="w-64">
                                <select
                                    id="user-select"
                                    value={userId}
                                    onChange={(e) => {
                                        const newUserId = Number(e.target.value);
                                        setUserId(newUserId);
                                        if (newUserId !== 0 && onlyMy) {
                                            setOnlyMy(false);
                                        }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value={0}>All Users</option>
                                    {propsUsers.filter((u) => u.type === 'case_manager' && u.active).map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.name || u.email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Only my checkbox - only show for admins */}
                        {isAdmin && (
                            <label className="w-auto flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={onlyMy}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setOnlyMy(checked);
                                        if (checked && userId !== 0) {
                                            setUserId(0);
                                        }
                                    }}
                                    className="cursor-pointer"
                                />
                                <span className="text-sm text-gray-600">Only mine</span>
                            </label>
                        )}

                        <span className="text-sm text-gray-500 ml-auto">
                            {sortedPatients.length} patients
                        </span>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {renderPatientsTab()}
                </div>
            </div>

            {/* Manage Columns Modal */}
            <ManageColumnsModal
                isOpen={isManageColumnsOpen}
                onClose={() => setIsManageColumnsOpen(false)}
                columns={columnOrder.map(id => ({
                    id,
                    label: ALL_COLUMNS.find(col => col.id === id)?.label || id,
                    visible: columnVisibility[id] ?? true,
                }))}
                onSave={handleSaveColumns}
                onReset={handleResetColumns}
            />

            {/* Save As View Modal */}
            <SaveViewModal
                isOpen={isSaveViewModalOpen}
                onClose={() => setIsSaveViewModalOpen(false)}
                onSave={handleSaveAsView}
                currentConfig={{
                    columnOrder,
                    columnWidths,
                    columnVisibility,
                    sortColumn,
                    sortOrder,
                }}
                isAdmin={isAdmin}
                isSaving={isCreating}
                existingDefaultView={defaultView}
            />

            {/* Meeting Count Modal */}
            <MeetingCountModal
                isOpen={isMeetingCountModalOpen}
                onClose={() => {
                    setIsMeetingCountModalOpen(false);
                    setSelectedPatientForMeetings(null);
                }}
                caseManagerIntake={selectedPatientForMeetings?.meeting_count_text1 || "0"}
                psychiatricIntake={selectedPatientForMeetings?.meeting_count_text2 || "0"}
                psyFollowUp={selectedPatientForMeetings?.meeting_count_text3 || "0"}
                caseManagerFollowUp={selectedPatientForMeetings?.meeting_count_text4 || "0"}
            />

            <IssuesModal
                isOpen={isIssuesModalOpen}
                onClose={() => {
                    setIsIssuesModalOpen(false);
                    setSelectedPatientForIssues(null);
                }}
                issues={selectedPatientForIssues?.issues ? JSON.parse(selectedPatientForIssues.issues) : []}
                getUserName={getUserName}
            />

            {/* Edit Comments Modal */}
            <EditCommentsModal
                isOpen={isEditCommentsModalOpen}
                onClose={() => {
                    setIsEditCommentsModalOpen(false);
                    setSelectedPatientForEdit(null);
                }}
                initialNotes={selectedPatientForEdit?.task || ''}
                onSave={async (newNotes: string) => {
                    setIsSavingComments(true);
                    try {
                        await apiService.updatePatientComments(
                            token,
                            selectedPatientForEdit.id,
                            newNotes
                        );
                        await patientsQuery.refetch();
                        setIsEditCommentsModalOpen(false);
                        setSelectedPatientForEdit(null);
                    } catch (error: any) {
                        console.error('Failed to update comments:', error);
                        throw new Error(error.response?.data?.message || 'Failed to update comments');
                    } finally {
                        setIsSavingComments(false);
                    }
                }}
                isSaving={isSavingComments}
            />

            {/* Context Menu */}
            {contextMenu && (() => {
                const hasPhoneExt = user.phone_ext && user.phone_ext.trim() !== '';
                const patientPhone = contextMenu.patient.phone;

                return (
                    <ContextMenu
                        x={contextMenu.x}
                        y={contextMenu.y}
                        options={[
                            {
                                label: 'Edit',
                                icon: '✏️',
                                onClick: async () => {
                                    const patient = contextMenu.patient;
                                    setContextMenu(null);
                                    setIsLoadingPatientData(true);
                                    try {
                                        const patientData = await apiService.getPatientPage({
                                            token,
                                            patient_id: patient.id
                                        });
                                        setSelectedPatientForEditInfo(patientData);
                                        setIsEditPatientModalOpen(true);
                                    } catch (error) {
                                        console.error('Failed to fetch patient data:', error);
                                    } finally {
                                        setIsLoadingPatientData(false);
                                    }
                                }
                            },
                            ...(canScheduleMeeting(user.type) ? [{
                                label: 'New Meeting',
                                icon: '📅',
                                onClick: () => {
                                    setSelectedPatientForNewMeeting(contextMenu.patient);
                                    setIsNewMeetingModalOpen(true);
                                    setContextMenu(null);
                                }
                            }] : []),
                            {
                                label: 'View Meetings',
                                icon: '📋',
                                onClick: () => {
                                    setSelectedPatientForViewMeetings(contextMenu.patient);
                                    setIsViewMeetingsModalOpen(true);
                                    setContextMenu(null);
                                }
                            },
                            ...(contextMenu.patient.show_schedule_link ? [{
                                label: 'Open Schedule Page',
                                icon: '🗓️',
                                onClick: async () => {
                                    const patient = contextMenu.patient;
                                    setContextMenu(null);
                                    try {
                                        const res = await apiService.getPatientScheduleLink(token, Number(patient.id));
                                        if (res?.link) {
                                            window.open(res.link, '_blank', 'noopener');
                                        }
                                    } catch (error) {
                                        console.error('Failed to get schedule link:', error);
                                    }
                                }
                            }] : []),
                            {
                                label: 'View Patient',
                                icon: '👤',
                                onClick: () => {
                                    const patient = contextMenu.patient;
                                    setContextMenu(null);
                                    onNavigate?.("patient", patient.id);
                                }
                            },
                            {
                                label: 'Finish Treatment',
                                icon: (
                                    <svg fill="#e23232" width="16px" height="16px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
                                    </svg>
                                ),
                                iconClassName: '',
                                onClick: () => {
                                    setSelectedPatientForFinishTreatment(contextMenu.patient);
                                    setIsFinishTreatmentModalOpen(true);
                                    setContextMenu(null);
                                }
                            },
                            {
                                label: 'Ad Hoc Meeting',
                                icon: '⚡',
                                onClick: () => {
                                    setSelectedPatientForAdHoc(contextMenu.patient);
                                    setContextMenu(null);
                                }
                            },
                            // Conference call to the patient, same as the "Call patient"
                            // button on the patient page.
                            ...(patientPhone ? [{
                                label: 'Call Patient',
                                icon: '📞',
                                onClick: () => {
                                    setSelectedPatientForConfCall(contextMenu.patient);
                                    setContextMenu(null);
                                }
                            }] : []),
                            ...(patientPhone ? [{
                                label: 'WhatsApp',
                                icon: '💬',
                                onClick: () => {
                                    setContextMenu(null);
                                    setWaPopupPatient({
                                        phone: normalizePhone(patientPhone),
                                        name: `${contextMenu.patient.first_name || ''} ${contextMenu.patient.last_name || ''}`.trim(),
                                        hmId: contextMenu.patient.HM_ID || contextMenu.patient.hm_id,
                                        id: contextMenu.patient.id,
                                    });
                                }
                            }] : []),
                            ...(hasPhoneExt && patientPhone ? [{
                                label: 'Call',
                                icon: '📞',
                                onClick: async () => {
                                    const patient = contextMenu.patient;
                                    if (patient && patient.phone) {
                                        setContextMenu(null);
                                        setStatusMessage({ type: 'loading', text: 'Initiating call...' });
                                        try {
                                            const response = await apiService.makeCall(token, patient.id, patient.main_phone);
                                            const successMsg = response.message || 'Call initiated successfully';
                                            setStatusMessage({ type: 'success', text: successMsg });
                                            setTimeout(() => setStatusMessage(null), 3000);
                                        } catch (error: any) {
                                            console.error('Failed to make call:', error);
                                            const errorMsg = error.message || error.response?.data?.message || 'An error occurred';
                                            setStatusMessage({ type: 'error', text: errorMsg });
                                            setTimeout(() => setStatusMessage(null), 5000);
                                        }
                                    }
                                }
                            }] : []),
                            ...(contextMenu.patient.status === 'Waiting-for-parent2-sign' ? [{
                                label: 'Parent 2 consent link',
                                icon: '🔗',
                                onClick: () => {
                                    const patient = contextMenu.patient;
                                    const consentLink = patient.parent_2_concent_link || '';
                                    if (consentLink) {
                                        navigator.clipboard.writeText(consentLink);
                                        setStatusMessage({ type: 'success', text: 'Link copied to clipboard' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    } else {
                                        setStatusMessage({ type: 'error', text: 'No consent link available' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    }
                                    setContextMenu(null);
                                }
                            }] : []),
                            ...(contextMenu.patient.status === 'Waiting-for-single-guardian-verification' ? [{
                                label: 'Single guardian verification link',
                                icon: '🔗',
                                onClick: () => {
                                    const patient = contextMenu.patient;
                                    const verificationLink = patient.single_guardian_link || '';
                                    if (verificationLink) {
                                        navigator.clipboard.writeText(verificationLink);
                                        setStatusMessage({ type: 'success', text: 'Link copied to clipboard' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    } else {
                                        setStatusMessage({ type: 'error', text: 'No verification link available' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    }
                                    setContextMenu(null);
                                }
                            }] : [])
                        ]}
                        onClose={() => setContextMenu(null)}
                    />
                );
            })()}

            {/* Loading Overlay */}
            {isLoadingPatientData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 flex flex-col items-center shadow-2xl">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                        <p className="text-gray-700 font-medium">Loading patient data...</p>
                    </div>
                </div>
            )}

            {/* New Meeting Modal */}
            <NewMeetingModal
                isOpen={isNewMeetingModalOpen}
                onClose={() => {
                    setIsNewMeetingModalOpen(false);
                    setSelectedPatientForNewMeeting(null);
                }}
                patient={selectedPatientForNewMeeting}
                users={propsUsers}
                meetingTypes={propsMeetingTypes}
                currentUser={user}
            />

            {/* Edit Patient Modal */}
            <EditPatientModal
                isOpen={isEditPatientModalOpen}
                onClose={() => {
                    setIsEditPatientModalOpen(false);
                    setSelectedPatientForEditInfo(null);
                }}
                patient={selectedPatientForEditInfo}
                onSave={async (updatedData: any) => {
                    setIsSavingPatientInfo(true);
                    try {
                        await apiService.updatePatientInfo(
                            token,
                            selectedPatientForEditInfo.id,
                            updatedData
                        );
                        await patientsQuery.refetch();
                        setIsEditPatientModalOpen(false);
                        setSelectedPatientForEditInfo(null);
                    } catch (error: any) {
                        console.error('Failed to update patient info:', error);
                        throw new Error(error.response?.data?.message || error.message || 'Failed to update patient information');
                    } finally {
                        setIsSavingPatientInfo(false);
                    }
                }}
                isSaving={isSavingPatientInfo}
            />

            {/* View Meetings Modal */}
            <ViewMeetingsModal
                isOpen={isViewMeetingsModalOpen}
                onClose={() => {
                    setIsViewMeetingsModalOpen(false);
                    setSelectedPatientForViewMeetings(null);
                }}
                patientId={selectedPatientForViewMeetings?.id || ''}
                patientName={selectedPatientForViewMeetings ? `${selectedPatientForViewMeetings.first_name} ${selectedPatientForViewMeetings.last_name}` : ''}
                user={user}
                token={token}
                users={propsUsers as any}
                meetingTypes={propsMeetingTypes}
            />

            {/* Finish Treatment Modal */}
            <FinishTreatmentModal
                isOpen={isFinishTreatmentModalOpen}
                onClose={() => {
                    setIsFinishTreatmentModalOpen(false);
                    setSelectedPatientForFinishTreatment(null);
                }}
                patientId={selectedPatientForFinishTreatment?.id || 0}
                token={token}
                onSuccess={() => {
                    patientsQuery.refetch();
                }}
            />

            {/* Ad Hoc Meeting Modal - like the patient page */}
            {selectedPatientForAdHoc && (
                <AdHocMeetingModal
                    patientId={Number(selectedPatientForAdHoc.id)}
                    token={token}
                    meetingTypes={propsMeetingTypes}
                    onClose={() => setSelectedPatientForAdHoc(null)}
                    onSuccess={(meetingId) => {
                        const patientId = selectedPatientForAdHoc.id;
                        setStatusMessage({ type: 'success', text: 'Meeting created successfully!' });
                        setTimeout(() => setStatusMessage(null), 3000);
                        // Open the patient page with the new meeting selected
                        onNavigate?.("patient", patientId, meetingId);
                    }}
                    onError={(msg) => {
                        setStatusMessage({ type: 'error', text: msg });
                        setTimeout(() => setStatusMessage(null), 5000);
                    }}
                />
            )}

            {/* Call Patient (conference) Modal - like the patient page */}
            {selectedPatientForConfCall && (
                <CallPatientConfModal
                    patientId={selectedPatientForConfCall.id}
                    patientName={`${selectedPatientForConfCall.first_name || ''} ${selectedPatientForConfCall.last_name || ''}`.trim()}
                    patientPhone={selectedPatientForConfCall.phone}
                    parent1Phone={selectedPatientForConfCall.parent_1_phone_number}
                    parent2Phone={selectedPatientForConfCall.parent_2_phone_number}
                    token={token}
                    onClose={() => setSelectedPatientForConfCall(null)}
                    onSuccess={() => {
                        setStatusMessage({ type: 'success', text: 'Call initiated successfully!' });
                        setTimeout(() => setStatusMessage(null), 3000);
                    }}
                    onError={(msg) => {
                        setStatusMessage({ type: 'error', text: msg });
                        setTimeout(() => setStatusMessage(null), 5000);
                    }}
                />
            )}

            {/* Status Message Bar */}
            {statusMessage && (
                <div
                    className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg"
                    style={{
                        backgroundColor:
                            statusMessage.type === 'error' ? '#fee2e2' :
                                statusMessage.type === 'success' ? '#dcfce7' :
                                    '#f3f4f6',
                        color:
                            statusMessage.type === 'error' ? '#dc2626' :
                                statusMessage.type === 'success' ? '#16a34a' :
                                    '#6b7280'
                    }}
                >
                    {statusMessage.text}
                </div>
            )}
            {waPopupPatient && (
                <WhatsAppConversationPopup
                    phoneNumber={waPopupPatient.phone}
                    patientName={waPopupPatient.name}
                    patientHmId={waPopupPatient.hmId}
                    patientId={waPopupPatient.id}
                    currentUserId={Number(user.id)}
                    onClose={() => setWaPopupPatient(null)}
                />
            )}
        </Layout>
    );
};
